// src/services/patientSurvey.js
//
// The health survey sent from the patient page (Files → "Send survey text", flag
// PFT_SURVEY_TEXT_ENABLED, 2026-10-26). Firebase-free and tested (patientSurvey.test.js).
// The server owns every rule (functions/pftSurveyPatient.js): who can be texted, the
// text, and what an answer fills in. This file only turns its state into what the
// page shows, and keeps an open page from saving over what an answer added.
//
// State comes from pft_survey_patient_status/{store}__{id} (server-written), or, for a
// patient moved from PFT Uploads before this existed, from their card's `survey`.

import { formatSentAt, surveyOf } from "./pftSurvey";

/** Which store the survey works on for this page, or null (Duplicates: never). */
export const patientSurveyStore = ({ source, inDuplicates }) => {
  if (inDuplicates || source === "duplicates") return null;
  if (source === "sheet") return "sheet";
  if (source === "firestore") return "consents";
  return null;
};

/** The card fallback: the newest survey of this patient's PFT imports, or null. */
export const cardSurveyFor = (imports = [], { store, id } = {}) =>
  surveyOf((imports || []).filter((i) => i && i.patientId === id && (i.patientStore || "consents") === store));

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * What the Files section shows.
 *   show          anything to show at all (a patient with a file who was never texted: nothing)
 *   state         "none" | "texted" | "responded"
 *   canSend       the button is offered (no file yet, not answered)
 *   buttonLabel   "Send survey text" | "Text again"
 *   sentLine      "Texted Oct 26, 2026, 2:43 PM by a@x (2 times)"
 *   answeredLine  "Answered Oct 27, 2026, 9:10 AM"
 *   attachNeeded  answered, but the answers aren't on the patient yet ("Attach now").
 *                 submissionIds: the survey submissions already on the record
 *   open / decided  the differences still to check, and those already decided
 */
export const surveyPanelView = ({ status = null, card = null, hasFile = false, submissionIds = [] } = {}) => {
  const s = status || null;
  const c = !s ? card : null;
  const src = s || c || {};
  const state = src.status === "responded" ? "responded" : src.recipientId ? "texted" : "none";
  const conflicts = Array.isArray(s && s.conflicts) ? s.conflicts.filter((x) => x && x.field) : [];
  // Who texted and when: the patient's own record, else the PFT Uploads card the text
  // went out from (a patient moved before answering, 2026-10-26).
  const sent = s && s.lastSentAt ? s : card && card.lastSentAt ? card : null;
  const viaCard = !!sent && (sent === card || sent.sentFrom === "card");
  const count = Number(sent && sent.sendCount) || 0;
  const sentLine = sent
    ? `Texted ${formatSentAt(sent.lastSentAt)}${sent.lastSentBy ? ` by ${sent.lastSentBy}` : ""}${count > 1 ? ` (${plural(count, "time")})` : ""}${viaCard ? ", from Review PFT Patients" : ""}`
    : "";
  const answeredLine = state === "responded" && src.respondedAt ? `Answered ${formatSentAt(src.respondedAt)}` : state === "responded" ? "Answered" : "";
  // A card moved WITH its answers already carries them (pftSurveySubmissionId).
  const attachNeeded =
    state === "responded" && (!!(s && s.applyError) || (!s && !!c && !(submissionIds || []).includes(c.submissionId)));
  const canSend = !hasFile && state !== "responded";
  return {
    show: canSend || state !== "none",
    state,
    canSend,
    buttonLabel: state === "texted" ? "Text again" : "Send survey text",
    sentLine,
    answeredLine,
    attachNeeded,
    applied: (s && s.applied) || null,
    open: conflicts.filter((x) => !x.resolved),
    decided: conflicts.filter((x) => x.resolved),
    lastAppliedAt: (s && s.lastAppliedAt) || "",
  };
};

// ---- Keeping an open page in step -------------------------------------------------------
// An answer (or a "Use survey") can change the patient while their page is open. The
// page then takes the stored value for every field the person hasn't touched, and keeps
// their own edits: notes keep both (the answer only ever appends), diagnoses keep both
// (it only ever adds). Without this, Save would write the page's older copy back.

const text = (v) => (v === undefined || v === null ? "" : String(v));

/** One field: page value, value at load (base), stored value now. */
export const mergeSurveyField = (field, page, base, stored) => {
  if (field === "diagnosis") {
    const p = Array.isArray(page) ? page : [];
    const b = Array.isArray(base) ? base : [];
    const s = Array.isArray(stored) ? stored : [];
    return [...p, ...s.filter((d) => !b.includes(d) && !p.includes(d))];
  }
  if (field === "notes") {
    const p = text(page);
    const b = text(base);
    const s = text(stored);
    if (p === s) return p;
    if (p === b) return s;
    if (s === b) return p;
    if (s.startsWith(b)) return `${p}${s.slice(b.length)}`;
    // Both changed in ways that can't be lined up: keep both rather than lose either.
    return `${p}\n\n${s}`;
  }
  return text(page) === text(base) ? stored : page;
};

/** The fields an answer can change, as the patient page holds them (consents doc or Sheet patient). */
export const surveyFieldsOfRecord = (p = {}) => ({
  phone: p.phone || "",
  physicalAddress: p.physicalAddress || "",
  email: p.email || "",
  bmi: p.bmi || "",
  notes: p.notes || "",
  diagnosis: Array.isArray(p.diagnosis) ? p.diagnosis : p.diagnosis ? [p.diagnosis] : [],
  fileUrl: p.consentUrl || p.fileUrl || "",
});

/** The survey submissions already on a record (a merge's list, or a PFT Uploads move's one). */
export const surveySubmissionIdsOfRecord = (p = {}) =>
  [...(Array.isArray(p.pftSurveySubmissionIds) ? p.pftSurveySubmissionIds : []), p.pftSurveySubmissionId].filter(Boolean);

// CRC page (2026-10-29): in the "Pending Medical Records" box ONLY, the card of a
// patient who answered the survey text turns green. Answered = their record already
// carries the answers (a submission id), or their status doc says "responded" (an
// answer that couldn't attach). Every other box is unchanged.
export const PENDING_MEDICAL_RECORDS = "Pending Medical Records";

/** The pft_survey_patient_status doc id of a CRC-page patient (RegX or Sheet). */
export const surveyStatusKey = (p = {}) =>
  p.source === "sheet" ? `sheet__${p.token || ""}` : `consents__${p.id || ""}`;

/** Green on the CRC page: only in Pending Medical Records, only once they answered. */
export const surveyAnsweredOnCard = (patient, box, respondedKeys = new Set()) =>
  String(box || "").trim().toLowerCase() === PENDING_MEDICAL_RECORDS.toLowerCase() &&
  !!patient &&
  (surveySubmissionIdsOfRecord(patient).length > 0 || respondedKeys.has(surveyStatusKey(patient)));

// The fields an answer or a "Use survey" can write (page state names).
export const SURVEY_PAGE_FIELDS = ["phone", "physicalAddress", "email", "bmi", "notes", "diagnosis", "fileUrl"];

/** { field: merged } for every field, from the page, the loaded baseline and the stored record. */
export const mergeSurveyChanges = (page, base, stored) =>
  Object.fromEntries(SURVEY_PAGE_FIELDS.map((f) => [f, mergeSurveyField(f, page[f], base[f], stored[f])]));
